import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Briefcase, 
  Users, 
  TrendingUp, 
  Plus,
  Eye,
  Edit,
  Trash2,
  Filter,
  Search,
  Calendar,
  MapPin,
  DollarSign,
  Building
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';

const CompanyDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    pendingApplications: 0
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch dashboard stats
      const statsResponse = await axios.get('/api/company/dashboard/stats', { withCredentials: true });
      setStats(statsResponse.data);

      // Fetch recent job postings
      const jobsResponse = await axios.get('/api/company/jobs/recent', { withCredentials: true });
      setRecentJobs(jobsResponse.data);

      // Fetch recent applications
      const applicationsResponse = await axios.get('/api/company/applications/recent', { withCredentials: true });
      setRecentApplications(applicationsResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job posting?')) {
      try {
        await axios.delete(`/api/company/jobs/${jobId}`, { withCredentials: true });
        toast.success('Job deleted successfully');
        fetchDashboardData(); // Refresh data
      } catch (error) {
        toast.error('Failed to delete job');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-secondary-200 dark:bg-secondary-700 rounded w-1/4 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 bg-secondary-200 dark:bg-secondary-700 rounded-lg"></div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="h-96 bg-secondary-200 dark:bg-secondary-700 rounded-lg"></div>
              <div className="h-96 bg-secondary-200 dark:bg-secondary-700 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white mb-2">
            Welcome back, {user?.name}!
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400">
            Manage your job postings and review applications from talented candidates.
          </p>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <div className="bg-white dark:bg-secondary-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Briefcase className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Total Jobs
                </p>
                <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                  {stats.totalJobs}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-secondary-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Active Jobs
                </p>
                <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                  {stats.activeJobs}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-secondary-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Total Applications
                </p>
                <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                  {stats.totalApplications}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-secondary-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                <Calendar className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Pending Reviews
                </p>
                <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                  {stats.pendingApplications}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white dark:bg-secondary-800 rounded-xl p-6 shadow-lg mb-8"
        >
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/company/jobs/create"
              className="flex items-center p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors duration-200"
            >
              <Plus className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-3" />
              <span className="font-medium text-secondary-900 dark:text-white">
                Post New Job
              </span>
            </Link>
            <Link
              to="/company/applications"
              className="flex items-center p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors duration-200"
            >
              <Users className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-3" />
              <span className="font-medium text-secondary-900 dark:text-white">
                View Applications
              </span>
            </Link>
            <Link
              to="/company/profile"
              className="flex items-center p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors duration-200"
            >
              <Building className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-3" />
              <span className="font-medium text-secondary-900 dark:text-white">
                Company Profile
              </span>
            </Link>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Job Postings */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg"
          >
            <div className="p-6 border-b border-secondary-200 dark:border-secondary-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
                  Recent Job Postings
                </h2>
                <Link
                  to="/company/jobs"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                >
                  View All
                </Link>
              </div>
            </div>
            <div className="p-6">
              {recentJobs.length > 0 ? (
                <div className="space-y-4">
                  {recentJobs.slice(0, 5).map((job) => (
                    <div
                      key={job.id}
                      className="p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-secondary-900 dark:text-white mb-1">
                            {job.title}
                          </h3>
                          <div className="flex items-center space-x-4 text-xs text-secondary-500 dark:text-secondary-400 mb-2">
                            <div className="flex items-center">
                              <MapPin className="w-3 h-3 mr-1" />
                              {job.location}
                            </div>
                            <div className="flex items-center">
                              <DollarSign className="w-3 h-3 mr-1" />
                              {job.salary_min} - {job.salary_max}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              job.is_active 
                                ? 'text-green-600 bg-green-100 dark:bg-green-900/20' 
                                : 'text-red-600 bg-red-100 dark:bg-red-900/20'
                            }`}>
                              {job.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <span className="text-xs text-secondary-500 dark:text-secondary-400">
                              {job.applications_count} applications
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <Link
                            to={`/company/jobs/${job.id}/edit`}
                            className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors duration-200"
                          >
                            <Edit className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => handleDeleteJob(job.id)}
                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-secondary-500 dark:text-secondary-400 text-center py-8">
                  No job postings yet. Create your first job posting!
                </p>
              )}
            </div>
          </motion.div>

          {/* Recent Applications */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg"
          >
            <div className="p-6 border-b border-secondary-200 dark:border-secondary-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
                  Recent Applications
                </h2>
                <Link
                  to="/company/applications"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                >
                  View All
                </Link>
              </div>
            </div>
            <div className="p-6">
              {recentApplications.length > 0 ? (
                <div className="space-y-4">
                  {recentApplications.slice(0, 5).map((application) => (
                    <div
                      key={application.id}
                      className="p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-secondary-900 dark:text-white mb-1">
                            {application.student_name}
                          </h3>
                          <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-2">
                            Applied for: {application.job_title}
                          </p>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              application.status === 'applied' ? 'text-blue-600 bg-blue-100 dark:bg-blue-900/20' :
                              application.status === 'shortlisted' ? 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20' :
                              application.status === 'interview' ? 'text-purple-600 bg-purple-100 dark:bg-purple-900/20' :
                              application.status === 'accepted' ? 'text-green-600 bg-green-100 dark:bg-green-900/20' :
                              'text-red-600 bg-red-100 dark:bg-red-900/20'
                            }`}>
                              {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                            </span>
                            <span className="text-xs text-secondary-500 dark:text-secondary-400">
                              {new Date(application.applied_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <Link
                          to={`/company/applications/${application.id}`}
                          className="ml-4 p-2 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors duration-200"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-secondary-500 dark:text-secondary-400 text-center py-8">
                  No applications yet. Applications will appear here once students apply to your jobs.
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboard;
